import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileTypeIcon } from '@/components/Icons/FileTypeIcon';
import type { MailMessage } from '@/types/mail';
import { formatDateTime } from '@/utils/date';
import { formatFileSize, getFileType } from '@/utils/file';
import { sanitizeRichTextHtml } from '@/utils/html';
import { joinNonEmpty } from '@/utils/text';

interface Props {
  mail: MailMessage;
}

export const MailPdfDocument: React.FC<Props> = ({ mail }) => {
  const { t } = useTranslation();

  const hasRecipients = mail.recipients.length > 0;
  const hasAttachments = mail.attachments.length > 0;
  const preparedDate = new Date().toLocaleDateString('lv-LV');
  const recipientNames = joinNonEmpty(
    mail.recipients.map((recipient) => recipient.name),
  );

  return (
    <div className="w-[794px] bg-white px-10 py-10 text-foreground">
      <h1 className="text-headline-20 text-primary">{mail.subject}</h1>

      <div className="my-4 border-t border-divider" />

      <div className="flex flex-col gap-1 text-body-14">
        <div className="flex gap-2">
          <span className="w-20 shrink-0 text-subtle-foreground">
            {t('mail.detail.pdf.from')}:
          </span>
          <span className="flex-1">
            {mail.senderName || t('mail.detail.pdf.unknownSender')}
          </span>
        </div>
        {hasRecipients && (
          <div className="flex gap-2">
            <span className="w-20 shrink-0 text-subtle-foreground">
              {t('mail.detail.to')}
            </span>
            <span className="flex-1">{recipientNames}</span>
          </div>
        )}
        <div className="flex gap-2">
          <span className="w-20 shrink-0 text-subtle-foreground">
            {t('mail.detail.pdf.date')}:
          </span>
          <span className="flex-1">{formatDateTime(mail.timeCreated)}</span>
        </div>
      </div>

      <div className="my-4 border-t border-divider" />

      <p className="mb-2 text-subhead-14 text-subtle-foreground">
        {t('mail.detail.pdf.body')}
      </p>
      <div
        className="rich-text text-body-16"
        dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(mail.body) }}
      />

      {hasAttachments && (
        <>
          <p className="mt-8 mb-3 text-subhead-14 text-subtle-foreground">
            {t('mail.detail.pdf.attachments')}
          </p>
          <div className="flex flex-col gap-2">
            {mail.attachments.map((attachment) => (
              <div key={attachment.id} className="flex items-center gap-3">
                <FileTypeIcon
                  type={getFileType(attachment.name)}
                  className="size-8 shrink-0"
                />
                <span className="text-body-14">{attachment.name}</span>
                <span className="text-body-13 text-subtle-foreground">
                  {formatFileSize(attachment.sizeInBytes)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <p className="mt-10 text-caption-12 text-subtle-foreground">
        {t('mail.detail.pdf.footer', { date: preparedDate })}
      </p>
    </div>
  );
};
